/* ============================================================================
   UI — the intranet component library.
   Thin, typed React wrappers over the design-system CSS classes. Styling lives
   once, in Styles/design-system.css; these components only compose class names
   so every instance is guaranteed symmetric and on-system. Import from "Ui".
   ============================================================================ */
import React from "react";

const cx = (...a) => a.filter(Boolean).join(" ");

/* ── Actions ──────────────────────────────────────────────────────────────── */
export function Button({ variant, size, leftIcon, rightIcon, className, children, ...props }) {
  return (
    <button
      className={cx("ds-btn", variant && `ds-btn--${variant}`, size && `ds-btn--${size}`, className)}
      {...props}
    >
      {leftIcon}{children}{rightIcon}
    </button>
  );
}

/* ── State ────────────────────────────────────────────────────────────────── */
export function Badge({ variant, dot, className, children, ...props }) {
  return (
    <span className={cx("ds-badge", variant && `ds-badge--${variant}`, className)} {...props}>
      {dot && <span className="ds-badge__dot" />}{children}
    </span>
  );
}

/* ── Form ─────────────────────────────────────────────────────────────────── */
export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx("ds-input", className)} {...props} />;
});

export function Field({ label, hint, error, htmlFor, children }) {
  return (
    <div className="ds-field">
      {label && <label className="ds-label" htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <span className="ds-field__error">{error}</span>
        : hint ? <span className="ds-field__hint">{hint}</span> : null}
    </div>
  );
}

/* ── Containers ───────────────────────────────────────────────────────────── */
export function Card({ title, subtitle, actions, className, children, ...props }) {
  return (
    <section className={cx("ds-card", className)} {...props}>
      {(title || actions) && (
        <div className="ds-card__head">
          <div>
            {title && <h3 className="ds-card__title">{title}</h3>}
            {subtitle && <p className="ds-card__sub">{subtitle}</p>}
          </div>
          {actions && <div className="ds-toolbar">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Section({ title, actions, children }) {
  return (
    <section className="ds-section">
      {(title || actions) && (
        <div className="ds-section__head">
          {title && <h3 className="ds-h3">{title}</h3>}
          <span style={{ flex: 1 }} />
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── Page scaffolding ─────────────────────────────────────────────────────── */
export function Page({ children, className }) {
  return <div className={cx("ds-page", className)}>{children}</div>;
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <header className="ds-page-header">
      <div>
        {eyebrow && <span className="ds-page-header__eyebrow">{eyebrow}</span>}
        <h1 className="ds-page-header__title">{title}</h1>
        {subtitle && <p className="ds-page-header__sub">{subtitle}</p>}
      </div>
      {actions && <div className="ds-page-header__actions">{actions}</div>}
    </header>
  );
}

/* ── Data ─────────────────────────────────────────────────────────────────── */
export function Stat({ label, value, hint }) {
  return (
    <div className="ds-stat">
      <span className="ds-stat__label">{label}</span>
      <span className="ds-stat__value">{value}</span>
      {hint && <span className="ds-card__sub">{hint}</span>}
    </div>
  );
}

export function StatGrid({ children }) {
  return <div className="ds-stat-grid">{children}</div>;
}

/**
 * columns: [{ key, header, align?, render?(row) }]
 * rows:    array of objects (optional `id` for keys)
 * loading: true while the rows are still on their way, so "empty" is not shown as if it were the answer
 */
export function Table({ columns, rows = [], empty = "Nothing to show.", loading = false }) {
  return (
    <div className="ds-tablewrap">
      <table className="ds-table">
        <thead>
          <tr>{columns.map((c) => (
            <th key={c.key} style={c.align ? { textAlign: c.align } : undefined}>{c.header}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="ds-muted" style={{ textAlign: "center", padding: "28px" }}>{loading ? "Loading…" : empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id ?? i}>{columns.map((c) => (
              <td key={c.key} style={c.align ? { textAlign: c.align } : undefined}>{c.render ? c.render(r) : r[c.key]}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
